<template>
  <div v-if="currentStep" class="tutorial-overlay">
    <div class="tutorial-backdrop" @click="skipTutorial" />

    <div v-if="targetElement" ref="highlightRef" class="tutorial-highlight">
      <div class="tutorial-content" :class="[contentPosition, { 'in-drawer': isInDrawer }]" :style="contentStyle">
        <div class="text-h6">{{ currentStep.title }}</div>
        <div class="text-body1 q-my-md">{{ currentStep.description }}</div>
        <div class="row justify-between">
          <div>
            <q-btn v-if="canGoPrevious" flat :icon="symRoundedArrowBack" label="Previous" @click="previousStep" />
          </div>
          <div class="row q-gutter-sm">
            <q-btn flat :icon="symRoundedClose" label="Skip" color="grey" @click="skipTutorial" />
            <q-btn :icon="isLastStep ? symRoundedCheck : symRoundedArrowForward" :label="isLastStep ? 'Finish' : 'Next'" color="primary" @click="nextStep" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { symRoundedArrowBack, symRoundedArrowForward, symRoundedCheck, symRoundedClose } from '@quasar/extras/material-symbols-rounded';
import { useTutorialStore } from 'src/CrudManager/Core/stores/useTutorialStore';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';

const tutorialStore = useTutorialStore();
const highlightRef = ref<HTMLElement | null>(null);

const currentStep = computed(() => tutorialStore.currentStep);
const targetElement = ref<HTMLElement | null>(null);

const canGoPrevious = computed(() => tutorialStore.currentStepIndex > 0);
const isLastStep = computed(() => {
  if (!tutorialStore.activeTutorial || !tutorialStore.tutorials[tutorialStore.activeTutorial]) return true;
  return tutorialStore.currentStepIndex === tutorialStore.tutorials[tutorialStore.activeTutorial].steps.length - 1;
});

const contentPosition = computed(() => {
  return `position-${currentStep.value?.position || 'bottom'}`;
});

const isInDrawer = computed(() => {
  return targetElement.value?.closest('.q-drawer--left') !== null;
});

const contentStyle = computed(() => {
  if (!targetElement.value) return {};

  const rect = targetElement.value.getBoundingClientRect();
  const drawer = targetElement.value.closest('.q-drawer--left');

  if (drawer) {
    const drawerRect = drawer.getBoundingClientRect();
    const left = rect.left - drawerRect.left + rect.width / 2;
    return {
      position: 'fixed',
      left: `${left}px`,
      top: `${rect.bottom + 8}px`
    };
  }

  return {};
});

const updateHighlightPosition = () => {
  if (!targetElement.value || !highlightRef.value) return;

  const rect = targetElement.value.getBoundingClientRect();
  const highlight = highlightRef.value;
  const drawer = targetElement.value.closest('.q-drawer--left');

  if (drawer) {
    const drawerRect = drawer.getBoundingClientRect();
    highlight.style.top = `${rect.top}px`;
    highlight.style.left = `${rect.left - drawerRect.left}px`;
    highlight.style.width = `${rect.width}px`;
    highlight.style.height = `${rect.height}px`;
  }
  else {
    highlight.style.top = `${rect.top}px`;
    highlight.style.left = `${rect.left}px`;
    highlight.style.width = `${rect.width}px`;
    highlight.style.height = `${rect.height}px`;
  }
};

watch(() => currentStep.value?.selector,
  () => {
    if (!currentStep.value) return;
    targetElement.value = document.querySelector(currentStep.value.selector);
    updateHighlightPosition();
  });

onMounted(() => {
  window.addEventListener('resize', updateHighlightPosition);
  window.addEventListener('scroll', updateHighlightPosition);
});

onUnmounted(() => {
  window.removeEventListener('resize', updateHighlightPosition);
  window.removeEventListener('scroll', updateHighlightPosition);
});

const { nextStep, previousStep, skipTutorial } = tutorialStore;
</script>

<style lang="scss">
.tutorial-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9997;
  pointer-events: none;
}

.tutorial-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  pointer-events: auto;
}

.tutorial-highlight {
  position: absolute;
  background: transparent;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.5);
  border-radius: 4px;
  pointer-events: none;
  transition: all 0.3s ease;
  z-index: 9998;
}

.tutorial-content {
  position: absolute;
  background: white;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  pointer-events: auto;
  min-width: 280px;
  max-width: 400px;
  z-index: 9999;

  &.position-top {
    bottom: calc(100% + 12px);
    left: 50%;
    transform: translateX(-50%);
  }

  &.position-bottom {
    top: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
  }

  &.position-left {
    right: calc(100% + 12px);
    top: 50%;
    transform: translateY(-50%);
  }

  &.position-right {
    left: calc(100% + 12px);
    top: 50%;
    transform: translateY(-50%);
  }

  &.in-drawer {
    position: fixed;
    transform: none;
  }
}

.dark {
  .tutorial-content {
    background: $dark;
    color: white;
  }
}
</style>
